import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import 'assets/styles/Intake.css';
import {
  type IntakeFormData,
  type IntakeFormErrors,
  type YesNoType,
  type YesNoNaType,
  CANADIAN_PROVINCES,
  CORRECTION_TYPE_OPTIONS,
  DIABETIC_TYPE_OPTIONS,
  FAMILY_EYE_CONDITIONS,
  GENDER_OPTIONS,
  INITIAL_FORM_STATE,
  LENS_WEAR_DURATION_OPTIONS,
  MEDICAL_CONDITIONS,
  RELATIONSHIP_OPTIONS,
  VISION_SYMPTOMS,
  toggleArrayItem,
  validateForm,
} from 'models/IntakeModels';

// ─── Shared sub-components ────────────────────────────────────────────────────

interface YesNoRadioProps {
  value: string;
  onChange: (v: string) => void;
  includeNA?: boolean;
  hasError?: boolean;
}
const YesNoRadio: React.FC<YesNoRadioProps> = ({ value, onChange, includeNA = false, hasError }) => {
  const opts = includeNA ? ['Yes', 'No', 'N/A'] : ['Yes', 'No'];
  return (
    <div className="radio-group">
      {opts.map(opt => {
        const isSelected = value === opt;
        const colorClass = isSelected
          ? opt === 'Yes' ? 'selected-yes' : 'selected-no'
          : hasError ? 'error-radio' : '';
        return (
          <div key={opt} className={`radio-option ${colorClass}`} onClick={() => onChange(opt)}>
            <div className="radio-dot">
              {isSelected && <div className="radio-dot-inner" />}
            </div>
            {opt}
          </div>
        );
      })}
    </div>
  );
};

interface CheckboxGroupProps {
  options: string[];
  selected: string[];
  onToggle: (item: string) => void;
}
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ options, selected, onToggle }) => (
  <div className="checkbox-group">
    {options.map(opt => {
      const isChecked = selected.includes(opt);
      return (
        <div key={opt} className={`checkbox-option ${isChecked ? 'checked' : ''}`} onClick={() => onToggle(opt)}>
          <div className="custom-checkbox">
            {isChecked && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
          {opt}
        </div>
      );
    })}
  </div>
);

interface ClinicHeaderProps { name: string; address: string; }
const ClinicHeader: React.FC<ClinicHeaderProps> = ({ name, address }) => (
  <div className="clinic-header">
    <div className="clinic-logo">
      <div className="clinic-name">{name}</div>
    </div>
    <div className="clinic-tagline">{address}</div>
  </div>
);

interface SuccessPanelProps { firstName: string; }
const SuccessPanel: React.FC<SuccessPanelProps> = ({ firstName }) => (
  <div className="success-panel">
    <div className="success-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>Form Submitted!</h3>
    <p>
      Thank you, {firstName}. Your intake form has been received.<br />
      Our team will review your information shortly.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const IntakeForm: React.FC = () => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId]     = useState<string>(paramKeyId ?? '');

  const clinicName    = 'Dr. Azenda Optometry';
  const clinicAddress = '#1110-57 Avenue NE, AB, T2E 9B7';

  const [form, setForm]           = useState<IntakeFormData>(INITIAL_FORM_STATE);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors]       = useState<IntakeFormErrors>({});

  const update = <K extends keyof IntakeFormData>(key: K, value: IntakeFormData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleCheck = (
    key: 'medicalConditions' | 'familyEyeConditions' | 'visionSymptoms',
    item: string,
  ) => setForm(prev => ({ ...prev, [key]: toggleArrayItem(prev[key], item) }));

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) setSubmitted(true);
  };

  useEffect(() => {
    if (paramKeyId) setKeyId(paramKeyId);
  }, [paramKeyId]);

  return (
    <div className="page-wrapper">
      <ClinicHeader name={clinicName} address={clinicAddress} />

      <div className="form-card">
        <div className="form-title-bar">
          <h2>Patient Intake Form</h2>
          <span className="badge">Confidential</span>
        </div>

        <div className="form-body">
          {submitted ? (
            <SuccessPanel firstName={form.firstName} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              {/* ══ 1. Personal Information ══════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Personal Information</div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>First Name <span className="clinic-req">*</span></label>
                    <input className={errors.firstName ? 'error' : ''} placeholder="Jane"
                      value={form.firstName} onChange={e => update('firstName', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Last Name <span className="clinic-req">*</span></label>
                    <input className={errors.lastName ? 'error' : ''} placeholder="Smith"
                      value={form.lastName} onChange={e => update('lastName', e.target.value)} />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Date of Birth <span className="clinic-req">*</span></label>
                    <input type="date" className={errors.dob ? 'error' : ''}
                      value={form.dob} onChange={e => update('dob', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Mobile Number <span className="clinic-req">*</span></label>
                    <input type="tel" className={errors.mobile ? 'error' : ''} placeholder="(555) 000-0000"
                      value={form.mobile} onChange={e => update('mobile', e.target.value)} />
                  </div>
                </div>

                <div className="field mb-14">
                  <label>Email Address <span className="clinic-req">*</span></label>
                  <input type="email" className={errors.email ? 'error' : ''} placeholder="jane.smith@email.com"
                    value={form.email} onChange={e => update('email', e.target.value)} />
                </div>

                {/* Gender */}
                <div className="field">
                  <label>Gender</label>
                  <div className="gender-group">
                    {GENDER_OPTIONS.map(opt => (
                      <div
                        key={opt}
                        className={`gender-pill ${form.gender === opt ? 'selected' : ''}`}
                        onClick={() => update('gender', form.gender === opt ? '' : opt)}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ══ 2. Health Card ═══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Health Card</div>
                <div className="field">
                  <label>Health Card Number</label>
                  <input placeholder="e.g. 1234-567-890"
                    value={form.healthCard} onChange={e => update('healthCard', e.target.value)} />
                </div>
              </div>

              {/* ══ 3. Home Address ══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Home Address</div>

                <div className="field mb-14">
                  <label>Street Address</label>
                  <input placeholder="123 Maple Street, Apt 4B"
                    value={form.address} onChange={e => update('address', e.target.value)} />
                </div>

                <div className="grid-3">
                  <div className="field col-span-2">
                    <label>City</label>
                    <input placeholder="Calgary"
                      value={form.city} onChange={e => update('city', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Province</label>
                    <select value={form.province} onChange={e => update('province', e.target.value)}>
                      <option value="">—</option>
                      {CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field mt-14 mw-160">
                  <label>Postal Code</label>
                  <input placeholder="T2A 1B3"
                    value={form.postalCode} onChange={e => update('postalCode', e.target.value)} />
                </div>
              </div>

              {/* ══ 4. Emergency Contact ═════════════════════════════════════════ */}
              <div className="section section--emergency">
                <div className="section-label">Emergency Contact</div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Full Name <span className="clinic-req">*</span></label>
                    <input
                      className={errors.emergencyContactName ? 'error' : ''}
                      placeholder="John Smith"
                      value={form.emergencyContactName}
                      onChange={e => update('emergencyContactName', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Relationship to Patient</label>
                    <input
                      placeholder="e.g. Spouse, Parent, Sibling"
                      value={form.emergencyContactRelationship}
                      onChange={e => update('emergencyContactRelationship', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Primary Phone <span className="clinic-req">*</span></label>
                    <input
                      type="tel"
                      className={errors.emergencyContactPhone ? 'error' : ''}
                      placeholder="(555) 000-0000"
                      value={form.emergencyContactPhone}
                      onChange={e => update('emergencyContactPhone', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Alternate Phone</label>
                    <input
                      type="tel"
                      placeholder="(555) 000-0000"
                      value={form.emergencyContactAltPhone}
                      onChange={e => update('emergencyContactAltPhone', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ══ 5. Health Insurance ══════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Health Insurance</div>

                <div className="field mb-14">
                  <label>Health Insurer Name</label>
                  <input placeholder="e.g. Sun Life, Blue Cross, Manulife"
                    value={form.insurerName} onChange={e => update('insurerName', e.target.value)} />
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Policy Number</label>
                    <input placeholder="POL-000000"
                      value={form.policyNumber} onChange={e => update('policyNumber', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Group Number</label>
                    <input placeholder="GRP-0000"
                      value={form.groupNumber} onChange={e => update('groupNumber', e.target.value)} />
                  </div>
                </div>

                <div className="field">
                  <label>Relationship to Policyholder</label>
                  <select value={form.relationship}
                    onChange={e => update('relationship', e.target.value as IntakeFormData['relationship'])}>
                    <option value="">Select…</option>
                    {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* ══ 6. Visit Information ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Visit Information</div>

                <div className="field mb-14">
                  <label>Reason for Today's Visit</label>
                  <textarea placeholder="e.g. Routine eye exam, blurry vision, contact lens fitting…"
                    value={form.visitReason} onChange={e => update('visitReason', e.target.value)} />
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Date of Last Eye Exam</label>
                    <input type="date"
                      value={form.lastEyeExam} onChange={e => update('lastEyeExam', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Current Vision Correction</label>
                    <select value={form.currentCorrectionType}
                      onChange={e => update('currentCorrectionType', e.target.value as IntakeFormData['currentCorrectionType'])}>
                      <option value="">Select…</option>
                      {CORRECTION_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                {(form.currentCorrectionType === 'Contact Lenses' ||
                  form.currentCorrectionType === 'Glasses' ||
                  form.currentCorrectionType === 'Both') && (
                  <div className="field">
                    <label>How long have you been wearing {form.currentCorrectionType}?</label>
                    <select value={form.lensWearDuration}
                      onChange={e => update('lensWearDuration', e.target.value)}>
                      <option value="">Select…</option>
                      {LENS_WEAR_DURATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* ══ 7. Current Vision Symptoms ═══════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Current Vision Symptoms</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>Select all that apply:</p>
                <CheckboxGroup
                  options={VISION_SYMPTOMS}
                  selected={form.visionSymptoms}
                  onToggle={item => toggleCheck('visionSymptoms', item)}
                />
                {form.visionSymptoms.length > 0 && !form.visionSymptoms.includes('None') && (
                  <div className="field mt-10">
                    <label>Additional symptom details (optional)</label>
                    <textarea placeholder="Describe when symptoms occur, how long, severity, etc."
                      value={form.otherVisionSymptoms}
                      onChange={e => update('otherVisionSymptoms', e.target.value)} />
                  </div>
                )}
              </div>

              {/* ══ 8. Medical History ═══════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Medical History</div>

                {/* Diabetes */}
                <div className="field mb-14">
                  <label>Are you diabetic? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={form.isDiabetic}
                    onChange={v => update('isDiabetic', v as YesNoType)}
                    hasError={!!errors.isDiabetic} />
                  {form.isDiabetic === 'Yes' && (
                    <div className="detail-box">
                      <div className="detail-row">
                        <div>
                          <label>Diabetes Type</label>
                          <select value={form.diabeticType}
                            onChange={e => update('diabeticType', e.target.value as IntakeFormData['diabeticType'])}>
                            <option value="">Select…</option>
                            {DIABETIC_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label>Most Recent Blood Sugar (mmol/L)</label>
                          <input placeholder="e.g. 5.6"
                            value={form.bloodSugarLevel}
                            onChange={e => update('bloodSugarLevel', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pregnancy */}
                <div className="field mb-14">
                  <label>Are you currently pregnant? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={form.isPregnant}
                    onChange={v => update('isPregnant', v as YesNoNaType)}
                    includeNA hasError={!!errors.isPregnant} />
                </div>

                {/* Smoker */}
                <div className="field mb-14">
                  <label>Do you smoke? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={form.isSmoker}
                    onChange={v => update('isSmoker', v as YesNoType)}
                    hasError={!!errors.isSmoker} />
                </div>

                {/* Eye Surgery */}
                <div className="field mb-14">
                  <label>Have you had any previous eye surgery? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={form.hasEyeSurgery}
                    onChange={v => update('hasEyeSurgery', v as YesNoType)}
                    hasError={!!errors.hasEyeSurgery} />
                  {form.hasEyeSurgery === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the surgery (type, date, eye affected)</label>
                      <textarea placeholder="e.g. LASIK on both eyes, 2019…"
                        value={form.eyeSurgeryDetails}
                        onChange={e => update('eyeSurgeryDetails', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Eye Injury */}
                <div className="field mb-14">
                  <label>Have you had any eye injury or trauma? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={form.hasEyeInjury}
                    onChange={v => update('hasEyeInjury', v as YesNoType)}
                    hasError={!!errors.hasEyeInjury} />
                  {form.hasEyeInjury === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the injury</label>
                      <textarea placeholder="e.g. Foreign object in left eye, 2021…"
                        value={form.eyeInjuryDetails}
                        onChange={e => update('eyeInjuryDetails', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Current Medications */}
                <div className="field mb-14">
                  <label>Current Medications</label>
                  <textarea placeholder="List all prescription and over-the-counter medications, including eye drops…"
                    value={form.currentMedications}
                    onChange={e => update('currentMedications', e.target.value)} />
                </div>

                {/* Allergies */}
                <div className="field mb-14">
                  <label>Known Allergies</label>
                  <input placeholder="e.g. Penicillin, latex, eye drops…"
                    value={form.allergies}
                    onChange={e => update('allergies', e.target.value)} />
                </div>

                {/* Medical Conditions */}
                <div className="field">
                  <label>
                    Do you have any of the following conditions?{' '}
                    <span style={{ color: '#a0aec0', fontWeight: 400 }}>(select all that apply)</span>
                  </label>
                  <div className="mb-8" />
                  <CheckboxGroup
                    options={MEDICAL_CONDITIONS}
                    selected={form.medicalConditions}
                    onToggle={item => toggleCheck('medicalConditions', item)}
                  />
                  <div className="field mt-10">
                    <label>Other medical conditions not listed above</label>
                    <input placeholder="e.g. Lupus, MS, Sjogren's…"
                      value={form.otherMedicalConditions}
                      onChange={e => update('otherMedicalConditions', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ══ 9. Family Eye History ════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Eye History</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>
                  Has anyone in your immediate family been diagnosed with:
                </p>
                <CheckboxGroup
                  options={FAMILY_EYE_CONDITIONS}
                  selected={form.familyEyeConditions}
                  onToggle={item => toggleCheck('familyEyeConditions', item)}
                />
                <div className="field mt-10">
                  <label>Other family eye conditions</label>
                  <input placeholder="e.g. Retinitis pigmentosa…"
                    value={form.otherFamilyEyeConditions}
                    onChange={e => update('otherFamilyEyeConditions', e.target.value)} />
                </div>
              </div>

              {/* ══ 10. Family Physician ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Physician</div>
                <div className="grid-2">
                  <div className="field">
                    <label>Family Doctor's Name</label>
                    <input placeholder="Dr. First Last"
                      value={form.familyDoctorName}
                      onChange={e => update('familyDoctorName', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Doctor's Phone Number</label>
                    <input type="tel" placeholder="(555) 000-0000"
                      value={form.familyDoctorPhone}
                      onChange={e => update('familyDoctorPhone', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ══ 11. Terms & Consent ══════════════════════════════════════════ */}
              <div
                className={['terms-block', form.terms ? 'checked' : '', errors.terms ? 'error-border' : ''].join(' ')}
                onClick={() => update('terms', !form.terms)}
              >
                <div className="custom-check">
                  {form.terms && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="terms-text">
                  I hereby consent to the disclosure of my eye care patient records by my health care
                  provider to {clinicName} for the purpose of fulfilling its obligation with respect to
                  secretarial and administrative support services, including management of patient files,
                  scheduling eye care appointments, issuing reminder notices, telephone answering, fee
                  collection (if applicable), and processing provincial health insurance fee claims (if
                  applicable). <span className="clinic-req">*</span>
                </div>
              </div>

              {/* ══ Submit ═══════════════════════════════════════════════════════ */}
              <button type="submit" className="submit-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                </svg>
                Submit Intake Form
              </button>

              <div className="privacy-notice">
                🔒 Your information is encrypted and protected under HIPAA / PHIPA.<br />
                This form is for new and returning patients of {clinicName}.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeForm;